import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingStatus } from './useOnboardingStatus';
import { useSpotlightElement } from './useSpotlightElement';
import { useTourPolling } from './useTourPolling';
import { onboardingService } from '../../services/onboardingService';
import { resolveActionPath } from '../../constants/onboardingStepNavigation';
import {
  filterTourSteps,
  isOnboardingTourStepEnabled,
} from '../../constants/onboardingTourExcludedSteps';
import { getStepTargets, type OnboardingStepTarget } from '../../constants/onboardingStepTargets';
import type { OnboardingStepItem } from '../../types/onboarding';

export type TourPhase = 'intro' | 'guided';

export interface SpotlightTourState {
  isActive: boolean;
  tourPhase: TourPhase;
  currentStep: OnboardingStepItem | null;
  currentStepIndex: number;
  totalSteps: number;
  spotlightRect: DOMRect | null;
  clipBounds: DOMRect | null;
  isModalTarget: boolean;
  currentTarget: OnboardingStepTarget | null;
  guidedTargetIndex: number;
  guidedTargetCount: number;
  canGoNextTarget: boolean;
  isClosingTour: boolean;
}

export interface SpotlightTourActions {
  handleGoToAction: () => Promise<void>;
  handleNextTarget: () => void;
  handleCompleteInteraction: () => void;
  syncGuidedTargetById: (targetId: string) => void;
  handleSkipStep: () => Promise<void>;
  handleCloseTour: () => Promise<void>;
  handleAdvanceToNextStep: () => Promise<void>;
  refetchOnboardingStatus: () => Promise<void>;
}

export type UseSpotlightTourResult = SpotlightTourState & SpotlightTourActions;

function findFirstPendingStep(steps: OnboardingStepItem[]): OnboardingStepItem | null {
  return steps.find((step) => step.status === 'pending') ?? null;
}

function hasPendingSteps(steps: OnboardingStepItem[]): boolean {
  return filterTourSteps(steps).some((step) => step.status === 'pending');
}

function resolveInitialStep(
  steps: OnboardingStepItem[],
  tourCurrentStepId: string | null
): OnboardingStepItem | null {
  const tourSteps = filterTourSteps(steps);
  const firstPending = findFirstPendingStep(tourSteps);
  if (!firstPending) {
    return null;
  }

  if (!tourCurrentStepId || !isOnboardingTourStepEnabled(tourCurrentStepId)) {
    return firstPending;
  }

  const savedStep = tourSteps.find((step) => step.id === tourCurrentStepId);
  if (!savedStep || savedStep.status !== 'pending') {
    return firstPending;
  }

  const savedIndex = tourSteps.findIndex((step) => step.id === tourCurrentStepId);
  const firstPendingIndex = tourSteps.findIndex((step) => step.id === firstPending.id);

  // Novo passo bloqueante inserido antes do progresso salvo (ex.: ACCESS_CONTROL antes de TEAM).
  if (firstPendingIndex < savedIndex && firstPending.blocking) {
    return firstPending;
  }

  return savedStep;
}

function findNextPendingStep(
  steps: OnboardingStepItem[],
  currentStepId: string
): OnboardingStepItem | null {
  const tourSteps = filterTourSteps(steps);
  const currentIndex = tourSteps.findIndex((step) => step.id === currentStepId);
  if (currentIndex < 0) {
    return null;
  }
  for (let index = currentIndex + 1; index < tourSteps.length; index += 1) {
    if (tourSteps[index].status === 'pending') {
      return tourSteps[index];
    }
  }
  return null;
}

function resetGuidedPhase(
  setTourPhase: (phase: TourPhase) => void,
  setGuidedTargetIndex: (index: number) => void
) {
  setTourPhase('intro');
  setGuidedTargetIndex(0);
}

export function useSpotlightTour(
  onTourComplete?: () => void,
  onError?: (message: string) => void
): UseSpotlightTourResult {
  const navigate = useNavigate();
  const { data, isLoading, refetch, dismissStep, tourDismissed, tourCurrentStepId } =
    useOnboardingStatus();

  const [currentStep, setCurrentStep] = useState<OnboardingStepItem | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [tourPhase, setTourPhase] = useState<TourPhase>('intro');
  const [guidedTargetIndex, setGuidedTargetIndex] = useState(0);
  const [isClosingTour, setIsClosingTour] = useState(false);
  const previousStepStatusRef = useRef<string | null>(null);
  const initializedRef = useRef(false);
  const reportErrorRef = useRef(onError);

  const steps = data?.steps ?? [];
  const tourSteps = useMemo(() => filterTourSteps(steps), [steps]);
  const totalSteps = tourSteps.length;

  const stepTargets = useMemo(
    () => (currentStep ? getStepTargets(currentStep.id) : []),
    [currentStep]
  );

  const currentTarget =
    tourPhase === 'guided' && stepTargets.length > 0
      ? (stepTargets[guidedTargetIndex] ?? null)
      : null;

  const canGoNextTarget =
    guidedTargetIndex < stepTargets.length - 1 &&
    !currentTarget?.requiresInteraction &&
    !currentTarget?.advanceProgrammaticallyOnly;

  const { spotlightRect, clipBounds, isModalTarget } = useSpotlightElement(
    isActive && tourPhase === 'guided' ? (currentTarget?.id ?? null) : null
  );

  const currentStepIndex = useMemo(() => {
    if (!currentStep) {
      return 0;
    }
    const index = tourSteps.findIndex((step) => step.id === currentStep.id);
    return index >= 0 ? index + 1 : 0;
  }, [currentStep, tourSteps]);

  useEffect(() => {
    reportErrorRef.current = onError;
  }, [onError]);

  const reportError = useCallback((message: string) => {
    reportErrorRef.current?.(message);
  }, []);

  const refetchOnboardingStatus = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const advanceToStep = useCallback(
    async (nextStep: OnboardingStepItem | null) => {
      resetGuidedPhase(setTourPhase, setGuidedTargetIndex);

      if (!nextStep) {
        setIsActive(false);
        setCurrentStep(null);
        onTourComplete?.();
        return;
      }

      try {
        await onboardingService.updateTourProgress(nextStep.id);
        setCurrentStep(nextStep);
        previousStepStatusRef.current = nextStep.status;
      } catch {
        reportError('Não foi possível atualizar o progresso do tour.');
      }
    },
    [onTourComplete, reportError]
  );

  const handleStepComplete = useCallback(async () => {
    if (!currentStep) {
      return;
    }
    const nextStep = findNextPendingStep(steps, currentStep.id);
    await advanceToStep(nextStep);
  }, [advanceToStep, currentStep, steps]);

  useEffect(() => {
    if (isLoading || !data) {
      return;
    }

    if (tourDismissed || !hasPendingSteps(steps)) {
      setIsActive(false);
      setCurrentStep(null);
      resetGuidedPhase(setTourPhase, setGuidedTargetIndex);
      initializedRef.current = false;
      return;
    }

    if (initializedRef.current) {
      return;
    }

    const initialStep = resolveInitialStep(steps, tourCurrentStepId);
    if (!initialStep) {
      setIsActive(false);
      setCurrentStep(null);
      resetGuidedPhase(setTourPhase, setGuidedTargetIndex);
      initializedRef.current = false;
      return;
    }

    if (initialStep.id !== tourCurrentStepId) {
      void onboardingService.updateTourProgress(initialStep.id).catch(() => {
        reportErrorRef.current?.('Não foi possível atualizar o progresso do tour.');
      });
    }

    setCurrentStep(initialStep);
    setIsActive(true);
    resetGuidedPhase(setTourPhase, setGuidedTargetIndex);
    previousStepStatusRef.current = initialStep.status;
    initializedRef.current = true;
  }, [data, isLoading, steps, tourCurrentStepId, tourDismissed]);

  useEffect(() => {
    if (!isActive || !currentStep || !data) {
      return;
    }

    const updatedStep = steps.find((step) => step.id === currentStep.id);
    if (!updatedStep) {
      return;
    }

    const previousStatus = previousStepStatusRef.current;
    if (previousStatus === 'pending' && updatedStep.status === 'complete') {
      void handleStepComplete();
    }

    previousStepStatusRef.current = updatedStep.status;
    if (updatedStep.status !== currentStep.status) {
      setCurrentStep(updatedStep);
    }
  }, [currentStep, data, handleStepComplete, isActive, steps]);

  useEffect(() => {
    if (!isActive || !currentStep || isLoading) {
      return;
    }

    const firstPending = findFirstPendingStep(filterTourSteps(steps));
    if (!firstPending || firstPending.id === currentStep.id || !firstPending.blocking) {
      return;
    }

    const savedTourStepId = tourCurrentStepId ?? currentStep.id;
    const savedIndex = steps.findIndex((step) => step.id === savedTourStepId);
    const firstPendingIndex = steps.findIndex((step) => step.id === firstPending.id);

    // Só recua quando um passo bloqueante novo ficou pendente antes do progresso salvo no tour.
    if (savedIndex < 0 || firstPendingIndex >= savedIndex) {
      return;
    }

    resetGuidedPhase(setTourPhase, setGuidedTargetIndex);
    setCurrentStep(firstPending);
    previousStepStatusRef.current = firstPending.status;
    void onboardingService.updateTourProgress(firstPending.id).catch(() => {
      reportErrorRef.current?.('Não foi possível atualizar o progresso do tour.');
    });
  }, [currentStep, isActive, isLoading, steps, tourCurrentStepId]);

  useTourPolling(refetch, isActive);

  const handleGoToAction = useCallback(async () => {
    if (!currentStep) {
      return;
    }

    try {
      await onboardingService.updateTourProgress(currentStep.id);
      navigate(resolveActionPath(currentStep));
      setTourPhase('guided');
      setGuidedTargetIndex(0);
    } catch {
      reportError('Não foi possível ir para a ação do passo.');
    }
  }, [currentStep, navigate, reportError]);

  const handleNextTarget = useCallback(() => {
    if (!canGoNextTarget) {
      return;
    }
    setGuidedTargetIndex((index) => index + 1);
  }, [canGoNextTarget]);

  const handleCompleteInteraction = useCallback(() => {
    if (guidedTargetIndex >= stepTargets.length - 1) {
      return;
    }
    setGuidedTargetIndex((index) => index + 1);
  }, [guidedTargetIndex, stepTargets.length]);

  const syncGuidedTargetById = useCallback(
    (targetId: string) => {
      const index = stepTargets.findIndex((target) => target.id === targetId);
      if (index >= 0) {
        setGuidedTargetIndex(index);
      }
    },
    [stepTargets]
  );

  const handleSkipStep = useCallback(async () => {
    if (!currentStep || currentStep.blocking) {
      return;
    }

    try {
      await dismissStep(currentStep.id);
      const refreshed = await onboardingService.getStatus();
      const nextStep = findNextPendingStep(refreshed.steps, currentStep.id);
      await advanceToStep(nextStep);
    } catch {
      reportError('Não foi possível pular este passo.');
    }
  }, [advanceToStep, currentStep, dismissStep, reportError]);

  const handleCloseTour = useCallback(async () => {
    if (isClosingTour) {
      return;
    }

    setIsClosingTour(true);
    try {
      await onboardingService.dismissTour();
      await refetch();
      setIsActive(false);
      setCurrentStep(null);
      resetGuidedPhase(setTourPhase, setGuidedTargetIndex);
      navigate('/onboarding');
    } catch {
      reportError('Não foi possível fechar o tour.');
    } finally {
      setIsClosingTour(false);
    }
  }, [isClosingTour, navigate, refetch, reportError]);

  const handleAdvanceToNextStep = useCallback(async () => {
    if (!currentStep || !currentTarget?.advanceToNextStepOnFinish) {
      return;
    }

    const nextStep = findNextPendingStep(steps, currentStep.id);
    await advanceToStep(nextStep);
    await refetch();
  }, [advanceToStep, currentStep, currentTarget, refetch, steps]);

  return {
    isActive,
    tourPhase,
    currentStep,
    currentStepIndex,
    totalSteps,
    spotlightRect,
    clipBounds,
    isModalTarget,
    currentTarget,
    guidedTargetIndex,
    guidedTargetCount: stepTargets.length,
    canGoNextTarget,
    isClosingTour,
    handleGoToAction,
    handleNextTarget,
    handleCompleteInteraction,
    syncGuidedTargetById,
    handleSkipStep,
    handleCloseTour,
    handleAdvanceToNextStep,
    refetchOnboardingStatus,
  };
}
